import React, {memo} from 'react';
import {Stack} from '@mui/material';
import type {Data, SeparatedPart} from 'schemaDefinition/types';
import {isData} from 'schemaDefinition';
import {makePartPath} from 'schemaDefinition/functions/validators';
import {Ruler} from 'components/ruler';
import type {FormMode, UseValueById} from '../types';
import type {PartDiffProps} from './types';
// eslint-disable-next-line import-x/no-cycle
import {DataFormPartWrapper} from './DataFormPartWrapper';

type Props = {
  useValue: UseValueById;
  useOriginalValue?: UseValueById | undefined;
  valuePath: string;
  relatedScope: Data | undefined;
  fragment: SeparatedPart[];
  mode: FormMode;
  diff?: PartDiffProps;
  noLabel: boolean;
  reviewStatuses?: Data;
};
/**
 * Renders a vertical list of schema parts/sublist of parts,
 * where sublists of parts renders horizontally
 */
const DataFormSchemaFragmentComponent: React.FC<Props> = ({
  useValue,
  useOriginalValue,
  valuePath,
  relatedScope,
  fragment,
  mode,
  diff,
  noLabel,
  reviewStatuses,
}) => {
  const fragments = fragment.map((part, idx) => {
    if (!Array.isArray(part) && part.type === 'separator') {
      return part.form === 'line' &&
        ((idx > 0 && idx < fragment.length - 1) ||
          part.include === 'always') ? (
        <Ruler key={idx} spacing={1} />
      ) : null;
    }

    // Handle horizontal layout of parts, i.e. [part1, part2, part3]
    if (Array.isArray(part)) {
      return part.length > 0 ? (
        <Stack key={idx} direction={'row'} spacing={2}>
          <DataFormSchemaFragment
            useValue={useValue}
            useOriginalValue={useOriginalValue}
            valuePath={valuePath}
            relatedScope={relatedScope}
            fragment={part}
            mode={mode}
            diff={diff}
            noLabel={noLabel}
            reviewStatuses={reviewStatuses}></DataFormSchemaFragment>
        </Stack>
      ) : null;
    } else {
      const partValuePath = makePartPath(valuePath, part);
      const compareValue = isData(diff?.compareValue)
        ? (diff?.compareValue as Data)
        : undefined;
      const partDiff: PartDiffProps | undefined = diff
        ? ({
            ...diff,
            compareValue:
              compareValue && part.name ? compareValue[part.name] : null,
          } as const)
        : undefined;

      return (
        <DataFormPartWrapper
          key={part.name ?? part.role}
          part={part}
          useValue={useValue}
          relatedScope={relatedScope}
          valuePath={partValuePath}
          scopePath={undefined}
          usesGlobalScope={undefined}
          useOriginalValue={useOriginalValue}
          mode={mode}
          diff={partDiff}
          noLabel={noLabel}
        />
      );
    }
  });

  return fragments.length ? <>{fragments}</> : null;
};

// Only re-render when the active parts or values actually change
export const DataFormSchemaFragment = memo(DataFormSchemaFragmentComponent);
